import { useOktaAuth } from '@okta/okta-react';
import { useBoolean } from 'ahooks';
import type { History } from 'history';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { triggerOtp } from 'components/pages/two-factor-auth/utils';
import { RoutePaths } from 'constant';
import { ActiveTab, ApiKey, TokenType } from 'services';
import { getTokenMeta, PermissionType } from 'utils/token';
import { useQueryParams } from './use-query-params';

type Loading = boolean;
type Error = boolean;

export const redirectToSavedTab = (
  history: History<History.PoorMansUnknown>,
): void => {
  const savedTab = ActiveTab.pop();
  const pathname = savedTab ? new URL(savedTab)?.pathname ?? '/' : '/';
  const hash = savedTab ? new URL(savedTab)?.hash ?? '' : '';

  history.replace(`${pathname}${hash}`);
};

export const useLoginWithToken = (
  token: string,
  tokenType: TokenType,
): [Loading, Error] => {
  const history = useHistory();
  const { oktaAuth } = useOktaAuth();
  const [isError, setError] = useState(false);
  const [isLoading, { setTrue, setFalse }] = useBoolean(false);
  const params = useQueryParams();
  const externalRefKey = params.get('externalRefKey') ?? '';
  const projectId = params.get('projectId') ?? '';

  useEffect(() => {
    const loginWithoutOtp = async () => {
      setTrue();
      if (ApiKey.exists()) {
        await ApiKey.clear();
      }
      try {
        /**
         * Check if magic link contains hash to load the task drawer.
         */

        await ApiKey.create(token, tokenType, externalRefKey, projectId);
        oktaAuth.authStateManager.updateAuthState();
        setFalse();
        redirectToSavedTab(history);
      } catch (e) {
        setFalse();
        setError(true);
      }
    };

    const loginWithOtp = async () => {
      try {
        /**
         * Check if magic link contains hash to load the task drawer.
         */

        if (ApiKey.exists()) {
          redirectToSavedTab(history);
        } else {
          triggerOtp(token);
          history.push({
            pathname: RoutePaths.magicLink2fa,
            state: { token },
          });
        }
      } catch (e) {
        setError(true);
      }
    };

    const exec = () => {
      const { decoded } = getTokenMeta(token);

      const isOTPEnabled =
        decoded?.permission === PermissionType.UserOtp ?? false;

      if (isOTPEnabled) {
        loginWithOtp();
      } else {
        loginWithoutOtp();
      }
    };

    exec();
  }, [
    token,
    oktaAuth,
    setTrue,
    setFalse,
    history,
    tokenType,
    externalRefKey,
    projectId,
  ]);

  return [isLoading, isError];
};
